import React, { useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useToast } from '@common/toast';
import Icon from '@common/assets';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import { useUserInfo } from '@repository/auth/useUserInfo';
import useProfilePrize, { useSaveProfilePrize, viewKeys } from '@repository/profiles/useProfilePrize';
import prizeFormSchema from '@components/profile/profileOverlay/overlays/prize/prizeFormSchema';
import PrizeForm from '@components/profile/profileOverlay/overlays/prize/prizeWrite/prizeForm';
import ProfilePrizeVO from '@domain/vo/profile/ProfilePrizeVO';
import ProfilePrizeFormVO from '@domain/vo/profile/ProfilePrizeFormVO';
import Colors from '@domain/constant/colors';
import { queryClient } from '@utils/queryClient';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from './prize.module.scss';

const cx = classnames.bind(styles);

const prizeDefaultValues: RecursivePartial<ProfilePrizeFormVO> = {
  prizes: [],
};

interface IPrizeOverlayProps {
  visible: boolean;
  onClose: () => void;
}

const PrizeOverlay = ({ visible, onClose }: IPrizeOverlayProps) => {
  const Toast = useToast();
  const { data: userInfo } = useUserInfo();
  const { data: profilePrize } = useProfilePrize(!!userInfo?.id && visible);

  const { mutateAsync: saveProfilePrizeMutateAsync } = useSaveProfilePrize();

  const [exitModalVisible, setExitModalVisible] = useState(false);

  const methods = useForm<ProfilePrizeFormVO>({
    mode: 'onBlur',
    defaultValues: prizeDefaultValues as ProfilePrizeVO,
    resolver: yupResolver(prizeFormSchema) as any,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const onCloseOverlay = () => {
    if (isDirty) {
      setExitModalVisible(true);
      return;
    }

    onClose();
  };

  const onSubmit: SubmitHandler<ProfilePrizeFormVO> = (data) => {
    saveProfilePrizeMutateAsync(data)
      .then((result) => {
        if (result) {
          Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
          queryClient.refetchQueries(viewKeys, { active: true, exact: true });
        } else {
          Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
        }
      })
      .catch(() => {
        Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
      });
  };

  const onSubmitError: SubmitErrorHandler<ProfilePrizeFormVO> = (e) => {
    console.log(e);
    Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
  };

  useEffect(() => {
    if (profilePrize) {
      reset(new ProfilePrizeFormVO(profilePrize));
    }
  }, [profilePrize]);

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={onCloseOverlay} />
      <FormProvider {...methods}>
        <form
          onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
        >
          <div className={cx('overlayArea', { on: visible })}>
            <div className={cx('overlayInner')}>
              <div className={cx('overlayTitleArea')}>
                <strong className={cx('overlayTitle')}>수상 내역을 입력해주세요.</strong>
                <p className={cx('desc')}>
                  <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
                  입력한 수상 내역은 가장 최근에 활동했던 기준으로 자동 정렬됩니다.
                </p>
              </div>

              <div className={cx('scrollArea')}>
                <div className={cx('scrollInner')}>
                  <div className={cx('scrollWrap')}>
                    <PrizeForm />
                  </div>
                </div>
              </div>

              <div className={cx('btnArea')}>
                <a className={cx('btn', 'cancel')} role="button" onClick={onCloseOverlay}>
                  나가기
                </a>
                <button className={cx('btn', 'confirm')} type="submit">
                  저장
                </button>
              </div>
            </div>

            {/* 나가기 모달 영역 */}
            <ExitModal visible={exitModalVisible} onClose={() => setExitModalVisible(false)} onExit={onClose} />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default PrizeOverlay;
